"use client";
import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Camera, X, ImageIcon, ArrowRight } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import Image from "next/image";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface PhotoSearchSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoSearch: (file: File) => void;
}

// Dosya seçicide gösterilecek türler. MIME'ların yanına uzantılar da yazılır:
// Windows'ta .heic/.avif kayıtlı değilse ya da bazı Android dosya
// yöneticilerinde tarayıcı MIME eşlemesi yapamaz ve yalnız MIME yazılırsa
// dosyalar seçilemez (gri görünür).
const ACCEPT_ATTR = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/avif",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".heic",
  ".heif",
  ".avif",
].join(",");

// Aynı sebeple File.type boş string ("") gelebiliyor — özellikle HEIC/HEIF'te.
// Bu yüzden doğrulama MIME'a takılırsa uzantıya düşer.
const ACCEPTED_MIME = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
  "image/avif",
];
const ACCEPTED_EXT = /\.(jpe?g|png|webp|heic|heif|avif)$/i;
// HEIC/HEIF'i Safari dışındaki tarayıcılar decode edemez; dosya geçerli ama
// önizleme çizilemez. Bunu <Image onError> ile yakalayıp bilgi kartı gösteriyoruz.
const MAX_FILE_SIZE = 15 * 1024 * 1024;

const PhotoSearchSheet = ({ isOpen, onClose, onPhotoSearch }: PhotoSearchSheetProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tarayıcı görseli decode edemedi (pratikte Safari dışı tarayıcılarda HEIC/HEIF).
  const [previewFailed, setPreviewFailed] = useState(false);
  // Object URL'i revoke edebilmek için — state'e bakılırsa cleanup'ta eski
  // değeri okumak için dep listesi gerekirdi.
  const previewUrlRef = useRef<string | null>(null);
  // navigator sunucuda yok. Render sırasında okunursa sunucu false, client
  // hydration'da true üretir → hydration mismatch. Mismatch "recoverable" olsa
  // bile React tüm root'u client'ta yeniden render eder; bu sırada sayfadaki
  // Swiper'lar yeniden kurulduğu için ilk saniyelerde sürükleme çalışmaz.
  // Bu yüzden UA mount sonrası okunur.
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
  }, []);

  // Portal document.body'ye açılıyor (aşağıya bkz.) — document sunucuda yok.
  // Mount'a kadar null döndürmek sunucu ve ilk client render'ını eşitler,
  // yani hydration mismatch üretmez. Panel kapalıyken zaten görünmez olduğu
  // için mount'a kadar hiç render edilmemesinin görsel etkisi yok.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const clearSelection = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    setSelectedFile(null);
    setPreviewFailed(false);
  };

  const handleFile = (file: File) => {
    // MIME boş/bilinmiyorsa uzantıya bak (bkz. ACCEPTED_EXT yorumu).
    const isAccepted =
      ACCEPTED_MIME.includes(file.type.toLowerCase()) ||
      ACCEPTED_EXT.test(file.name);
    if (!isAccepted) {
      setError("Yalnızca JPG, PNG, WEBP, HEIC veya AVIF yükleyebilirsiniz.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("Dosya çok büyük. En fazla 15 MB yükleyebilirsiniz.");
      return;
    }
    clearSelection();
    setError(null);
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreview(url);
    setSelectedFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // input'u sıfırla — aynı dosya tekrar seçilebilsin
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Panel unmount olursa (ör. layout değişimi) son object URL'i bırakma.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  const handleClose = () => {
    clearSelection();
    setError(null);
    onClose();
  };

  const handleSearch = () => {
    if (!selectedFile) return;
    trackGeneric("search_photo", "click");
    onPhotoSearch(selectedFile);
    handleClose();
  };

  if (!mounted) return null;

  // document.body'ye portal: SearchInput hem masaüstü arama çubuğunda hem de
  // SearchMobileSheet içinde render ediliyor. O sheet transform + willChange
  // kullanıyor (SearchMobileSheet.tsx:38-40) ve transform'lu ata element
  // `fixed` için containing block oluşturur — portal olmadan panel viewport'a
  // değil sheet'e göre konumlanır, yani "sayfanın ortası" kayar.
  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
        style={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity 0.18s ease",
        }}
        onClick={handleClose}
      />

      {/* Sheet — mobil ve desktop'ta sayfanın tam ortası.
          Konumlandırma DIŞ katmanda (sadece sınıflarla), animasyon İÇ katmanda
          (sadece inline style ile). Ayrılmasaydı inline transform, Tailwind'in
          translate/konum sınıflarını ezerdi; ortalama da flex ile yapıldığı için
          animasyonun -50% translate ile uğraşması gerekmiyor.
          Dış katman pointer-events-none: tüm ekranı kapladığından aksi halde
          panel dışına tıklama overlay'e ulaşmaz ve "dışarı tıklayınca kapat"
          bozulurdu. */}
      <div className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center px-4 z-[9999] pointer-events-none">
        <div
          className="w-full max-w-md"
          style={{
            opacity: isOpen ? 1 : 0,
            transform: isOpen
              ? "translateY(0) scale(1)"
              : "translateY(-8px) scale(0.96)",
            pointerEvents: isOpen ? "auto" : "none",
            transition: "opacity 0.18s ease, transform 0.18s ease",
          }}
        >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center">
                    <Camera className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Fotoğrafla Ara</span>
                </div>
                <button
                  onClick={handleClose}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-gray-600" />
                </button>
              </div>

              <div className="p-5 space-y-3">
                {/* Gizli input'lar — her zaman DOM'da */}
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  className="hidden"
                  onChange={handleInputChange}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleInputChange}
                />

                {preview ? (
                  /* Önizleme */
                  <div className="space-y-3">
                    <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-50 border border-gray-200">
                      {previewFailed ? (
                        /* HEIC/HEIF gibi tarayıcının decode edemediği formatlar:
                           dosya geçerli, yalnızca önizlemesi çizilemiyor.
                           Kırık görsel yerine dosya bilgisini gösteriyoruz —
                           "Ara" yine çalışır. */
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
                          <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
                            <ImageIcon className="w-5 h-5 text-gray-400" />
                          </div>
                          <p className="text-sm font-medium text-gray-700 break-all line-clamp-2">
                            {selectedFile?.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            Fotoğraf yüklendi, önizlemesi bu tarayıcıda
                            gösterilemiyor.
                          </p>
                        </div>
                      ) : (
                        <Image
                          src={preview}
                          alt="Yüklenen fotoğraf"
                          fill
                          className="object-contain"
                          onError={() => setPreviewFailed(true)}
                        />
                      )}
                      <button
                        onClick={clearSelection}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                      >
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        onClick={clearSelection}
                        className="border-gray-200 text-gray-600 text-sm"
                      >
                        Değiştir
                      </Button>
                      <Button
                        className="text-sm gap-1.5"
                        onClick={handleSearch}
                      >
                        Ara
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Yükleme alanı */
                  <>
                    {/* Sürükle-bırak / tıkla alanı */}
                    <div
                      onClick={() => inputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      className={`
                        flex flex-col items-center justify-center gap-3
                        w-full h-40 rounded-xl border-2 border-dashed cursor-pointer
                        transition-colors select-none
                        ${isDragging
                          ? "border-gray-400 bg-gray-50"
                          : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50"
                        }
                      `}
                    >
                      <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Galeriden seç</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {isMobile ? "Tıkla veya sürükle" : "Tıkla veya sürükle bırak"}
                        </p>
                      </div>
                    </div>

                    {/* Kameradan çek — sadece mobilde */}
                    {isMobile && (
                      <button
                        onClick={() => cameraInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors text-sm text-gray-700 font-medium"
                      >
                        <Camera className="w-4 h-4" />
                        Kamera ile çek
                      </button>
                    )}

                    {/* Reddedilen dosya (tür/boyut) önceden sessizce yutuluyordu;
                        kullanıcı neden bir şey olmadığını göremiyordu. */}
                    {error ? (
                      <p className="text-[11px] text-red-500 text-center">
                        {error}
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-400 text-center">
                        Ürün veya kıyafet fotoğrafı yükleyerek benzer ürünleri bulabilirsiniz.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default PhotoSearchSheet;